import { LanguageSelector } from "./LanguageSelector";
import type { User } from "../../../../packages/shared/src/types";
import { t as msg } from "../lib/i18n";
import { useRef } from "react";
import {
  Layers,
  UsersRound,
  SquarePen,
  UserRound,
  Menu,
  ArrowUpRight,
} from "lucide-react";

const destinations = [
  {
    label: "My cards",
    href: "/cards",
    icon: Layers,
    matches: (path: string) => /^\/(cards|c|r)(\/|$)/.test(path),
  },
  {
    label: "Friends",
    href: "/friends",
    icon: UsersRound,
    matches: (path: string) => /^\/friends(\/|$)/.test(path),
  },
  {
    label: "Create",
    href: "/create",
    icon: SquarePen,
    matches: (path: string) => path === "/create" || path === "/templates",
  },
  {
    label: "Profile",
    href: "/profile",
    icon: UserRound,
    matches: (path: string) => path === "/profile" || path === "/guest-data",
  },
];

// One navigation model renders as a desktop bar or mobile tabs. Authentication
// belongs to the destination, so session checks never turn Profile into Sign in.
export function AppNavigation({ path, user, focused = false }: { path: string; user: User | null; focused?: boolean }) {
  const more = useRef<HTMLDetailsElement>(null);
  return (
    <header className={focused ? "site-header site-header-focused" : "site-header"}>
      <div className="wrap header-bar">
        <a className="brand" href="/">
          a promise card<span className="brand-dot">.</span>
        </a>
        {focused ? <a className="focused-home text-link" href="/cards">{msg("My cards")}</a> : <nav className="app-navigation" aria-label={msg("Main navigation")}>
          {destinations.map(({ label, href, icon: Icon, matches }) => (
            <a
              key={href}
              href={href}
              className={href === "/create" ? "nav-create" : undefined}
              aria-current={matches(path) ? "page" : undefined}
            >
              <Icon size={20} strokeWidth={1.7} aria-hidden="true" />
              <span>{msg(label)}</span>
            </a>
          ))}
        </nav>}
        <details
          className="site-extras"
          ref={more}
          onKeyDown={(event) => {
            if (event.key === "Escape" && more.current?.open) {
              more.current.open = false;
              more.current.querySelector("summary")?.focus();
            }
          }}
        >
          <summary aria-label={msg("About A Promise Card")}>
            <Menu size={21} aria-hidden="true" />
          </summary>
          <div className="site-extras-panel">
          <LanguageSelector user={user} />
          <nav aria-label={msg("Explore and support")}>
            <a href="/templates">
              {msg("Explore templates")}<ArrowUpRight size={16} aria-hidden="true" />
            </a>
            <a href="/#how-it-works">{msg("How it works")}</a>
            <a href="/contact">{msg("Help & contact")}</a>
            <a href="/privacy">{msg("Privacy")}</a>
          </nav>
          </div>
        </details>
      </div>
    </header>
  );
}
